import { useCallback } from 'react';

import { useGetMinistryPosts } from '@/features/api/hooks/grapevine-post/useGetMinistryPosts';
import { useGetRegionalfunkPosts } from '@/features/api/hooks/grapevine-post/useGetRegionalfunkPosts';
import { useGetPerson } from '@/features/api/hooks/participant/useGetPerson';
import { useRegionalfunkRadiusStore } from '@/features/geo-area/store/useRegionalfunkRadiusStore';
import { LAST_3_DAYS, LAST_14_DAYS } from '@/features/posts/constants/params';
import type { GenericPostDetail } from '@/features/posts/types/posts';

const isAtMostDaysOld = (created: number | undefined, threshold: number) =>
  created !== undefined && created >= threshold;

export type RegionNewsPostResult = {
  post: GenericPostDetail | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
};

// NOTE: fresh ministry → fresh regionalfunk → stale ministry (≤ 14d)
export const useRegionNewsPost = (): RegionNewsPostResult => {
  const radiusKm = useRegionalfunkRadiusStore((s) => s.meineRegionRadiusKm);
  const homeAreaId = useGetPerson().data?.homeAreaId;

  const ministryQuery = useGetMinistryPosts({ homeAreaId, count: 1 });
  const regionalfunkQuery = useGetRegionalfunkPosts({
    radiusKm,
    homeAreaId
  });

  const isLoading = ministryQuery.isLoading || regionalfunkQuery.isLoading;
  const isError = ministryQuery.isError || regionalfunkQuery.isError;

  const freshMinistryPost = ministryQuery.data?.find((p) =>
    isAtMostDaysOld(p.created, LAST_3_DAYS)
  );
  const freshRegionalfunkPost = regionalfunkQuery.data?.find((p) =>
    isAtMostDaysOld(p.created, LAST_3_DAYS)
  );

  const post =
    freshMinistryPost ??
    freshRegionalfunkPost ??
    ministryQuery.data?.find((p) => isAtMostDaysOld(p.created, LAST_14_DAYS));

  const { refetch: refetchMinistryPosts } = ministryQuery;
  const { refetch: refetchRegionalfunkPosts } = regionalfunkQuery;

  const refetch = useCallback(() => {
    refetchMinistryPosts();
    refetchRegionalfunkPosts();
  }, [refetchMinistryPosts, refetchRegionalfunkPosts]);

  return {
    post,
    isLoading,
    isError,
    refetch
  };
};
